import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Home, MessageSquareQuote, ShieldCheck } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Stars, StarInput } from "../../components/Stars";
import { DayChips, ProCard } from "./HomeBook";
import { Avatar } from "../../components/PhotoUpload";

function RateForm({ token, onDone }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const send = () => api.post(`/public/booking/${token}/rate`, { stars, comment }).then(() => { toast.success("Thank you for your rating!"); onDone(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="rounded-[var(--radius)] border border-primary/30 bg-primary/5 p-5 space-y-3" data-testid="home-rate-form">
      <p className="font-semibold">How was your practitioner?</p>
      <StarInput value={stars} onChange={setStars} testId="home-rate-star" />
      <Textarea rows={3} data-testid="home-rate-comment" placeholder="Tell others about your visit (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
      <Button className="rounded-full" data-testid="home-rate-submit" disabled={!stars} onClick={send}>Send rating</Button>
    </div>
  );
}

function SwitchPro({ token, date, lang, onDone }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(date);
  const [pros, setPros] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    if (!open) return;
    setPros(null); setSel(null);
    api.get(`/public/booking/${token}/alternatives`, { params: { date: d } }).then((r) => setPros(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [open, d, token]);
  const save = () => api.post(`/public/booking/${token}/switch`, { user_id: sel.pro.user_id, date: d, time: sel.time })
    .then(() => { toast.success("Practitioner changed"); setOpen(false); onDone(); }).catch((e) => toast.error(errMsg(e)));
  if (!open) return <Button variant="outline" className="rounded-full" data-testid="home-switch-open" onClick={() => setOpen(true)}>Choose another practitioner</Button>;
  return (
    <div className="rounded-[var(--radius)] border bg-card p-4 space-y-3" data-testid="home-switch-panel">
      <div className="flex items-center justify-between"><p className="font-semibold">Choose another practitioner</p>
        <Button size="sm" variant="ghost" data-testid="home-switch-close" onClick={() => setOpen(false)}>Close</Button></div>
      <DayChips value={d} lang={lang} testId="home-switch-day" onPick={setD} />
      {pros && !pros.length && <p className="text-sm text-muted-foreground" data-testid="home-switch-none">No other practitioners are free on this day. Try another day.</p>}
      {pros?.map((p) => <ProCard key={p.user_id} p={p} sel={sel} lang={lang} pick={(pro, time) => setSel({ pro, time })} />)}
      {sel && <Button className="rounded-full w-full" data-testid="home-switch-confirm" onClick={save}>Switch to {sel.pro.name} · {d} at {sel.time}</Button>}
    </div>
  );
}

export default function HomeVisitExtras({ token, lang, onChanged }) {
  const [x, setX] = useState(null);
  const load = useCallback(() => api.get(`/public/booking/${token}/home`).then((r) => setX(r.data)), [token]);
  useEffect(() => { load(); }, [load]);
  if (!x?.is_home) return null;
  return (
    <div className="space-y-4 mt-6" data-testid="home-visit-extras">
      <div className="flex items-center gap-3" data-testid="home-visit-pro"><Avatar url={x.professional_photo} name={x.professional_name} size="h-12 w-12" testId="home-visit-pro-photo" /><div className="text-sm"><div className="text-xs text-muted-foreground">Your practitioner</div><b>{x.professional_name}</b></div></div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="home-visit-address"><Home className="h-4 w-4" />Home visit · {x.address}, {x.postcode}</div>
      {x.job_status === "accepted" && <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="home-job-accepted"><ShieldCheck className="h-4 w-4" />Your practitioner has confirmed this visit.</div>}
      {x.can_rate && <RateForm token={token} onDone={load} />}
      {x.rating && <div className="rounded-[var(--radius)] border bg-card p-4 text-sm space-y-1" data-testid="home-your-rating"><div className="flex items-center gap-2">Your rating <Stars value={x.rating.stars} /></div>{x.rating.comment && <p className="text-muted-foreground italic">“{x.rating.comment}”</p>}</div>}
      {x.feedback && <div className="rounded-[var(--radius)] border bg-card p-4 text-sm space-y-1" data-testid="home-pro-feedback"><div className="flex items-center gap-2 font-semibold"><MessageSquareQuote className="h-4 w-4 text-primary" />A note from {x.feedback.professional_name}</div><p className="whitespace-pre-wrap">{x.feedback.comment}</p></div>}
      {x.can_switch && <SwitchPro token={token} date={x.date} lang={lang} onDone={() => { load(); onChanged(); }} />}
    </div>
  );
}
